<template>
  <v-card flat class="distance-rep-card">
    <v-card flat class="input-card pa-4">
      <v-chip-group v-if="!isCompetition" v-model="mode" row mandatory class="timer-mode-chips mb-4">
        <v-chip value="quick" outlined> ⚡ Schnellwahl </v-chip>
        <v-chip value="manual" outlined> 🔢 Manuell </v-chip>
      </v-chip-group>

      <template v-if="mode === 'quick' || isCompetition">
        <div class="section-title mb-2 white--text">Distanz wählen</div>
        <v-row dense no-gutters class="mb-4" :class="{ 'justify-content-center': isCompetition }">
          <v-col cols="4" v-for="(preset, idx) in distancePresets" :key="`d-${idx}`" class="pa-1">
            <v-btn
              block
              :color="isHyrox ? '#ffed00' : 'primary'"
              outlined
              class="preset-btn"
              :class="{ 'preset-selected-hyrox': isHyrox && distance === preset, 'preset-selected': !isHyrox && distance === preset }"
              @click="setDistance(preset)">
              {{ preset }} m
            </v-btn>
          </v-col>
        </v-row>

        <template v-if="hasWeightSetting">
          <div class="section-title mb-2 white--text">Gewicht wählen <span v-if="!isCompetition">(optional)</span></div>
          <v-row dense no-gutters class="mb-4" :class="{ 'justify-content-center': isCompetition }">
            <v-col cols="4" v-for="(preset, idx) in weightPresets" :key="`w-${idx}`" class="pa-1">
              <v-btn
                block
                outlined
                :color="isHyrox ? '#ffed00' : 'primary'"
                class="preset-btn"
                :class="{ 'preset-selected-hyrox': isHyrox && weight === preset, 'preset-selected': !isHyrox && weight === preset }"
                @click="setWeight(preset)">
                {{ preset }} kg
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </template>

      <template v-else-if="!isCompetition && mode === 'manual'">
        <v-row dense no-gutters class="mb-4 justify-space-between">
          <v-col :cols="hasWeightSetting ? 6 : 12" class="pr-2">
            <v-text-field
              v-model.number="distance"
              label="Distanz (m)"
              type="number"
              inputmode="numeric"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-map-marker-distance" />
          </v-col>
          <v-col v-if="hasWeightSetting" cols="6" class="pl-2">
            <v-text-field
              v-model.number="weight"
              label="Gewicht (kg)"
              type="number"
              inputmode="numeric"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-weight-lifter" />
          </v-col>
        </v-row>
      </template>

      <div class="button-container">
        <v-btn :color="isHyrox ? 'rgb(20, 20, 20)' : 'primary'" block class="add-set-btn elevation-2" :disabled="!isValid" @click="submit">
          {{ nextSetLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-card>
</template>

<script>
export default {
  name: "DistanceRepInput",
  props: ["exercise", "completedSets", "completedSetsArray", "planSettings", "isCompetition", "isHyrox"],
  data() {
    return {
      mode: "quick",
      distance: null,
      weight: null,
    }
  },
  computed: {
    hasWeightSetting() {
      // Prüft, ob die Übung grundsätzlich eine Gewichtseinstellung erlaubt
      return this.exercise?.set_exercise_settings?.some((s) => s.text === "Gewicht")
    },
    targetDistance() {
      const plannedDistance = this.planSettings?.find((s) => s.text === "Distanz")?.value
      if (plannedDistance) return Number(plannedDistance)
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => e.text === "Distanz")?.value) || 100
    },
    targetWeight() {
      if (!this.hasWeightSetting) return 0
      const plannedWeight = this.planSettings?.find((s) => s.text === "Gewicht")?.value
      if (plannedWeight) return Number(plannedWeight)
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => e.text === "Gewicht")?.value) || 0
    },
    distancePresets() {
      if (this.isCompetition) {
        return [this.targetDistance]
      }
      const center = Math.max(0, this.targetDistance)

      // Dynamischer Schritt basierend auf der Distanz
      let step
      if (center < 100) step = 10
      else if (center < 500) step = 25
      else if (center < 1000) step = 50
      else step = 100

      const presets = new Set()
      presets.add(center)
      presets.add(Math.max(0, center - 2 * step))
      presets.add(Math.max(0, center - step))
      presets.add(center + step)
      presets.add(center + 2 * step)
      presets.add(center + 3 * step) // Füge mehr Optionen hinzu, um sicher 6 zu haben

      return Array.from(presets)
        .filter((p) => p > 0 || (p === 0 && center === 0)) // 0 nur erlauben, wenn es das Ziel ist
        .sort((a, b) => a - b)
        .slice(0, 6)
    },
    weightPresets() {
      if (!this.hasWeightSetting) return []
      if (this.isCompetition) {
        // Gibt nur das Wettkampfgewicht zurück, falls vorhanden, sonst ein leeres Array
        return this.targetWeight !== null ? [this.targetWeight] : []
      }
      const center = Math.max(0, this.targetWeight)
      const step = 5
      const presets = new Set([
        center,
        Math.max(0, center - step),
        Math.max(0, center + step),
        Math.max(0, center - 2 * step),
        Math.max(0, center + 2 * step),
        0,
      ])
      return Array.from(presets)
        .filter((p) => p >= 0)
        .sort((a, b) => a - b)
        .slice(0, 6)
    },
    isValid() {
      return this.distance > 0
    },
    nextSetLabel() {
      const nextNumber = this.completedSetsArray.filter((set) => !set.skipped).length + 1
      return `${nextNumber}. Satz hinzufügen`
    },
  },
  methods: {
    setDistance(val) {
      this.distance = val
    },
    setWeight(val) {
      this.weight = val
    },
    submit() {
      if (!this.isValid) return
      const setPayload = [{ text: "Distanz", value: this.distance }]
      if (this.hasWeightSetting && this.weight !== null && this.weight >= 0) {
        setPayload.push({ text: "Gewicht", value: this.weight })
      }
      this.$emit("add-set", setPayload)
      this.initializeValues()
    },
    initializeValues() {
      this.distance = this.targetDistance
      this.weight = this.targetWeight
    },
  },
  watch: {
    planSettings: {
      handler() {
        this.initializeValues()
      },
      immediate: true,
      deep: true,
    },
  },
}
</script>

<style scoped>
/* Stile sind an weightRepInput.vue angelehnt für ein konsistentes Design */
.distance-rep-card {
  border-radius: 16px;
}
.input-card {
  background-color: rgba(44, 44, 46, 0.7);
  border-radius: 12px;
  padding-bottom: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
.timer-mode-chips .v-chip {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}
.preset-btn {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.2);
  min-height: 48px;
  font-size: 1rem;
  transition: all 0.3s ease;
}
.preset-selected {
  border-width: 2px;
  border-color: var(--v-primary) !important;
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.7) !important;
}
.add-set-btn {
  padding: 12px 24px;
  font-weight: 500;
  font-size: 1rem;
  border-radius: 24px;
  text-transform: none;
  width: 100%;
  max-width: 320px;
}
.preset-selected-hyrox {
  border-width: 2px;
  border-color: #ffed00 !important;
  color: #ffed00 !important;
  box-shadow: 0 0 15px rgba(255, 237, 0, 0.7) !important;
}
</style>
